export default (() => {
  const normalizeName = name => name.toLowerCase().replace(/[-_.]+/g, '-'),
        isPackageName = name => typeof name === 'string' && /^[\w.-]+$/.test(name),
        isPackageList = names => Array.isArray(names) && names.every(isPackageName),
        getRequirementName = requirement => normalizeName(requirement.match(/^[\w.-]+/)?.[0] || ''),
        isRuntime = runtime => ['reactPy', 'pyodide', 'micropip'].every(key => /^\d+\.\d+\.\d+$/.test(runtime?.[key])),
        isBootstrap = bootstrap => isPackageList(bootstrap) && ['micropip', 'pyodide-http'].every(name => bootstrap.includes(name)),
        isImportList = imports => Array.isArray(imports) && imports.every(name => typeof name === 'string' && /^[a-zA-Z_]\w*(\.[a-zA-Z_]\w*)*$/.test(name)),
        isRuntimeDependencies = dependencies => dependencies && typeof dependencies === 'object' &&
          Object.entries(dependencies).every(([name, depends]) => isPackageName(name) && isPackageList(depends)),
        getPackageRoots = ({ environment }) => [...environment.defaults.official, ...environment.defaults.micropip, ...environment.bootstrap]

  const validateConfiguration = ({ environment }) => {
    if(!environment || !Number.isInteger(environment.revision) || environment.revision < 1 ||
        !isRuntime(environment.runtime) ||
        !isPackageList(environment.defaults?.official) || !isPackageList(environment.defaults?.micropip) ||
        !isBootstrap(environment.bootstrap) || !Array.isArray(environment.requirements) ||
        !isImportList(environment.imports) || !isRuntimeDependencies(environment.runtimeDependencies)) {
      throw new Error('Invalid Python environment configuration')
    }
    const requirementNames = new Set()
    environment.requirements.forEach(requirement => {
      if(typeof requirement !== 'string' || !/^[\w.-]+==[^\s<>=!~]+$/.test(requirement)) {
        throw new Error(`Invalid generation requirement: ${requirement}`)
      }
      requirementNames.add(getRequirementName(requirement))
    })
    environment.defaults.micropip.forEach(name => {
      if(!requirementNames.has(normalizeName(name))) {
        throw new Error(`Missing generation requirement: ${name}`)
      }
    })
  }

  const validateLockedPackage = ({ name, entry }) => {
    if(!isPackageName(name) || normalizeName(name) !== name || !entry || typeof entry.version !== 'string' || !entry.version ||
        !['pyodide', 'pypi'].includes(entry.source) || !isPackageList(entry.depends) ||
        !/^[a-f0-9]{64}$/.test(entry.sha256) ||
        (entry.source === 'pypi' && (typeof entry.url !== 'string' || !/^https:\/\/[^\s?#]+\.whl$/.test(entry.url)))) {
      throw new Error(`Invalid locked package: ${name}`)
    }
  }

  const getLockedPackage = ({ packages, name }) => {
    if(!Object.hasOwn(packages, name)) {
      throw new Error(`Missing locked package: ${name}`)
    }
    return packages[name]
  }

  const validateRuntimeDependencies = ({ environment }) => {
    Object.entries(environment.runtimeDependencies).forEach(([name, depends]) => {
      const entry = getLockedPackage({ packages: environment.packages, name: normalizeName(name) })
      depends.map(normalizeName).forEach(dependency => {
        getLockedPackage({ packages: environment.packages, name: dependency })
        if(!entry.depends.includes(dependency)) {
          throw new Error(`Missing runtime dependency: ${name} -> ${dependency}`)
        }
      })
    })
  }

  const validatePackages = ({ environment }) => {
    if(!environment.packages || typeof environment.packages !== 'object' || Array.isArray(environment.packages)) {
      throw new Error('Invalid Python environment packages')
    }
    const requirePackage = name => getLockedPackage({ packages: environment.packages, name })

    Object.entries(environment.packages).forEach(([name, entry]) => {
      validateLockedPackage({ name, entry })
      entry.depends.forEach(requirePackage)
    })
    getPackageRoots({ environment }).map(normalizeName).forEach(requirePackage)
    validateRuntimeDependencies({ environment })
    environment.requirements.forEach(requirement => {
      const [name, version] = requirement.split('=='),
            entry = requirePackage(normalizeName(name))
      if(entry.version !== version) {
        throw new Error(`Generation requirement does not match locked package: ${name}`)
      }
    })
    if(environment.packages.micropip.version !== environment.runtime.micropip) {
      throw new Error('Micropip version does not match the Python environment')
    }
  }

  const validateEnvironment = ({ environment }) => {
    validateConfiguration({ environment })
    validatePackages({ environment })
  }

  const getPackageSelections = ({ packages }) => ['official', 'micropip'].flatMap(source =>
    (packages[source] || []).map(requirement => {
      const trimmedRequirement = requirement.trim()
      return {
        requirement,
        source,
        name: getRequirementName(trimmedRequirement),
        bare: isPackageName(trimmedRequirement)
      }
    })
  )

  const getRuntimePackages = ({ packages, environment }) => {
    validateEnvironment({ environment })
    const selections = getPackageSelections({ packages }),
          overrides = new Set(selections.filter(({ bare }) => !bare).map(({ name }) => name)),
          selectedNames = new Set(),
          custom = { official: [], micropip: [] }

    const visit = name => {
      if(!overrides.has(name) && !selectedNames.has(name)) {
        selectedNames.add(name)
        environment.packages[name].depends.forEach(visit)
      }
    }

    environment.bootstrap.map(normalizeName).forEach(visit)
    selections.forEach(({ requirement, name, source, bare }) => {
      if(bare && Object.hasOwn(environment.packages, name)) {
        visit(name)
      } else {
        custom[source].push(requirement)
      }
    })

    const names = [...selectedNames].sort()
    return {
      official: [...new Set([...names.filter(name => environment.packages[name].source === 'pyodide'), ...custom.official])],
      micropip: [...new Set([...names.filter(name => environment.packages[name].source === 'pypi').map(name => environment.packages[name].url), ...custom.micropip])]
    }
  }

  const createLockedPackages = ({ environment, frozen }) => {
    validateConfiguration({ environment })
    const packages = {}
    const visit = rawName => {
      const name = normalizeName(rawName)
      if(!Object.hasOwn(packages, name)) {
        const entry = frozen.packages[name]
        if(!entry) {
          throw new Error(`Missing resolved package: ${name}`)
        }
        const depends = [...new Set([...entry.depends, ...(environment.runtimeDependencies[name] || [])].map(normalizeName))].sort(),
              source = entry.file_name.startsWith('https://') ? 'pypi' : 'pyodide'
        packages[name] = {
          version: entry.version,
          source,
          depends,
          sha256: entry.sha256,
          ...(source === 'pypi' ? { url: entry.file_name } : {})
        }
        depends.forEach(visit)
      }
    }
    getPackageRoots({ environment }).forEach(visit)
    const sortedPackages = Object.fromEntries(Object.entries(packages).sort(([left], [right]) => left.localeCompare(right)))
    validatePackages({ environment: { ...environment, packages: sortedPackages } })
    return sortedPackages
  }

  const validateRuntime = ({ environment, reactPyVersion, workerSource, declaredReactPyVersion }) => {
    validateConfiguration({ environment })
    const embeddedVersion = workerSource.match(/importScripts\(['"]https:\/\/cdn\.jsdelivr\.net\/pyodide\/v([^/]+)\/full\/pyodide\.js['"]\)/)?.[1]
    if(reactPyVersion !== environment.runtime.reactPy || declaredReactPyVersion !== environment.runtime.reactPy) {
      throw new Error(`react-py must be pinned to ${environment.runtime.reactPy}`)
    }
    if(embeddedVersion !== environment.runtime.pyodide) {
      throw new Error(`react-py worker must use Pyodide ${environment.runtime.pyodide}`)
    }
  }

  const validateBundledPackages = ({ environment, bundled, names }) => {
    names.forEach(name => {
      const expected = environment.packages[name],
            actual = bundled.packages[name]
      if(actual?.version !== expected.version) {
        throw new Error(`Bundled version mismatch: ${name}`)
      }
      if(actual.sha256 !== expected.sha256) {
        throw new Error(`Bundled checksum mismatch: ${name}`)
      }
      const dependencies = actual.depends.map(normalizeName).sort()
      if(JSON.stringify(dependencies) !== JSON.stringify([...expected.depends].sort())) {
        throw new Error(`Bundled dependencies mismatch: ${name}`)
      }
    })
  }

  const sanitizeDiagnostics = text => text.replace(/https?:\/\/[^\s"'<>]+/gi, match => {
    const suffix = match.match(/[),.;]+$/)?.[0] || '',
          address = match.slice(0, match.length - suffix.length)
    try {
      const url = new URL(address)
      url.username = ''
      url.password = ''
      url.search = ''
      url.hash = ''
      return url.toString() + suffix
    } catch {
      return address.replace(/\/\/[^/]*@/, '//').split(/[?#]/)[0] + suffix
    }
  })

  const getFailedPackageName = ({ details, environment, packages }) => {
    const locked = Object.entries(environment?.packages || {}).find(([, entry]) => typeof entry?.url === 'string' && details.includes(sanitizeDiagnostics(entry.url))),
          missing = details.match(/(?:locked|resolved) package:\s*([\w.-]+)/i),
          selections = Array.isArray(packages?.micropip) ? packages.micropip : [],
          selectedUrl = selections.find(requirement => typeof requirement === 'string' && /^https?:\/\//i.test(requirement) && details.includes(sanitizeDiagnostics(requirement))),
          wheel = (selectedUrl || details).match(/(?:^|[/\s"'(])([\w.]+)-\d[^/\s"'<>]*\.whl\b/),
          named = details.match(/(?:download|fetch|load|install)(?:ing)?(?: package)? ['"]([\w.-]+)['"]/i)
    const name = locked?.[0] || missing?.[1] || wheel?.[1] || named?.[1]
    return name ? normalizeName(name) : undefined
  }

  const getInitializationError = ({ error, environment, packages }) => {
    const details = sanitizeDiagnostics(String(error?.message || error || 'Unknown initialization error')),
          conflict = details.match(/Requested ['"]([^'"]+)['"], but ([\w.-]+)==([^\s'"]+) is already installed/),
          missingWheel = details.match(/(?:Can't find a pure Python(?:\s+\d+)? wheel for|No matching distribution found for) ['"]?([^'"\s]+)/i)
    if(conflict) {
      const packageName = normalizeName(conflict[2]),
            requestedVersion = conflict[1].replace(/^[\w.-]+/, '')
      return { packageName, details, message: `${packageName} requires ${requestedVersion}, but version ${conflict[3]} is already installed. Edit packages to choose compatible versions.` }
    }
    if(missingWheel) {
      const packageName = getRequirementName(missingWheel[1])
      return { packageName, details, message: `${packageName} has no compatible Python wheel for this browser environment. Edit packages to choose a supported package or version.` }
    }
    const packageName = getFailedPackageName({ details, environment, packages })
    if(/Invalid Python environment|(?:Missing|Invalid) (?:locked|resolved) package|Missing (?:generation requirement|runtime dependency)|Generation requirement does not match|Micropip version does not match/.test(details)) {
      return { packageName, details, message: `The Python environment configuration is invalid${packageName ? ` for ${packageName}` : ''}. Try again after the application configuration has been corrected.` }
    }
    if(/Failed to fetch|NetworkError|HTTP\s*\d+|download|fetch|ERR_|offline|network/i.test(details)) {
      return { packageName, details, message: packageName
        ? `Python could not download ${packageName}. Check your connection and try again, or edit packages.`
        : 'Python could not download a required file. Check your connection and try again.' }
    }
    return { packageName, details, message: packageName
      ? `Python could not initialize ${packageName}. Try again, or edit packages.`
      : 'Python could not start. Try again, or edit packages if you recently changed them.' }
  }

  return { getRuntimePackages, validateEnvironment, createLockedPackages, validateRuntime, validateBundledPackages, getInitializationError }
})()
